import React, { memo, useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';

// Utilities
import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import {
    Section,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { telMetricsPhoneTextClick } from 'reaxl-analytics-handlers';
import { Email } from 'reaxl-email';
import { getEcommLinkWithWalletData } from 'reaxl-wallet';

import { getAbsolutePathWithListingId } from '@/utilities/vdpUrlBuilder';

import {
    authDuck,
    birfDuck,
    currentPageNameDuck,
    interestRateDuck,
    kbbVRSDataDuck,
    ownersDuck,
    paymentsDuck,
    preorderDuck,
    queryDuck,
    userDuck,
} from '@/ducks';

import {
    bapActiveModelDuck,
    bapActiveOwnerDuck,
    bapModelInfoDuck,
    bapSelectedOptionsDuck,
} from '@/ducks/bap';

import EmailOwnerFetcher from '@/fetchers/EmailOwnerFetcher';

import BuildAndPriceOwnerDetailsSnapshotContainer from './BuildAndPriceOwnerDetailsSnapshotContainer';
import getBapTrimId from './utilities/getBapTrimId';

const BuildAndPriceEmailOwnerContainer = memo(() => {
    const query = useSelector(queryDuck.selectors.getDuckState);
    const owners = useSelector(ownersDuck.selectors.getOwners);
    const preorderData = useSelector(preorderDuck.selectors.getPreorder);
    const listing = useMemo(() => {
        const preorderListings = preorderData || [];
        return preorderListings[0] || {}; // Use empty object as fallback if preorderListings[0] is undefined
    }, [preorderData]);
    const zip = query?.zip;
    const ecommercePaymentsInfo = useSelector(paymentsDuck.selectors.getEcommercePaymentsInfo) || {};
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const selectedOptions = useSelector(bapSelectedOptionsDuck.selectors.getDuckState) || {};
    const activeOwnerId = useSelector(bapActiveOwnerDuck.selectors.getDuckState) || '';
    const birf = useSelector(birfDuck.selectors.getPageBirf);
    const trimInfo = useSelector(kbbVRSDataDuck.selectors.getSelectedTrimInfo) || {};

    const makeCodeList = query?.makeCode || query?.makeCodeList;
    const makeCode = !Array.isArray(makeCodeList) && makeCodeList;
    const modelCode = useSelector(bapActiveModelDuck.selectors.getDuckState) || '';

    const kbbVRSData = useSelector(kbbVRSDataDuck.selectors.getData) || {};
    const selectedTrim = useSelector(bapModelInfoDuck.selectors.getDuckState);
    const trimsData = Object.values(_get(kbbVRSData, [makeCode, modelCode], []));
    const trimValue = trimsData.find((item) => getBapTrimId(item) === selectedTrim);

    // destructure listing and declare variables
    const {
        ecommWebsite = {},
    } = listing;

    let ecommHrefWithWalletData;
    if (ecommWebsite?.href) {
        const baseUrl = window?.location?.href;
        ecommHrefWithWalletData = getEcommLinkWithWalletData(ecommWebsite.href, ecommercePaymentsInfo, zip, baseUrl);
    }

    const activeOwner = owners?.[activeOwnerId];

    const { make = '', model = '', year = '', trimName = '' } = trimInfo;

    // set title include year, make, model and trimName
    listing.title = `${year} ${make} ${model} ${trimName}`;
    listing.selectedVrsData = kbbVRSData?.[makeCode]?.[modelCode]?.[selectedTrim];
    listing.trimSelected = trimValue?.trimName?.toLowerCase();

    const phoneClickHandler = useCallback((phoneNumber) => {
        const data = {
            birf,
            listing: {
                ...listing,
                vdpURL: getAbsolutePathWithListingId(window?.location?.origin, listing?.id),
                id: null,
            },
            phoneNumber,
        };
        sendClick(telMetricsPhoneTextClick, {}, data);
    }, [listing, birf]);

    const summaryContent = () => {
        const baseOptions = [
            {
                label: 'Make',
                name: 'makeCodeList',
                selectedOptions: [
                    {
                        label: make,
                        value: null,
                    },
                ],
            },
            {
                label: 'Model',
                name: 'modelCodeList',
                selectedOptions: [
                    {
                        label: model,
                        value: null,
                    },
                ],
            },
            {
                label: 'Trim',
                name: 'trimCodeList',
                selectedOptions: [
                    {
                        label: trimName,
                        value: null,
                    },
                ],
            },

        ];

        const getSelectedOptions = (options) => {
            if (Array.isArray(options)) {
                const selectedOptionsList = [];
                const optionNames = options?.map((option) => option.optionName);

                optionNames?.map((name) => selectedOptionsList.push(
                    {
                        label: name,
                        value: null,
                    },
                ));

                return selectedOptionsList;
            }

            return [
                {
                    label: options?.optionName,
                    value: null,
                },
            ];
        };

        const options = Object.keys(selectedOptions).map((key) => (
            {
                label: key === 'packageList' ? 'Accessory Equipment Packages' : selectedOptions[key]?.categoryGroup,
                name: key,
                selectedOptions: getSelectedOptions(selectedOptions[key]),
            }
        ));

        return { preferenceSelections: [...baseOptions, ...options] };
    };

    const emailProps = {
        listing,
        owner: activeOwner,
        uiContext: 'buildAndPrice',
        fetcher: EmailOwnerFetcher,
        isLoggedIn,
        preferenceSummary: summaryContent(),
        userDuck,
        interestRateDuck,
        currentPageNameDuck,
        enableConsumerMetadata: true,
    };

    return activeOwner?.id ? (
        <Section
            alignment="left"
            uiContext="section-2"
            heading="Get a Final Price"
            headingClassName="text-blue-darker text-size-lg-500 margin-bottom-5"
            className={clsx({
                'padding-horizontal-4': true,
                'padding-vertical-5': true,
            })}
        >
            <BuildAndPriceOwnerDetailsSnapshotContainer
                phoneClickHandler={phoneClickHandler}
                listing={{
                    ...listing,
                    owner: activeOwner,
                    ...(ecommHrefWithWalletData && {
                        ecommWebsite: {
                            ...listing.ecommWebsite,
                            href: ecommHrefWithWalletData,
                        },
                    }),

                }}
                uiContext={{
                    isPanel: true,
                    showColoredBackground: true,
                }}
            />
            <Email
                {...emailProps}
            />
        </Section>
    ) : null;
}, _isEqual);

export default BuildAndPriceEmailOwnerContainer;
